import React from 'react';
import { PAGED_LEASING_INTEREST_TYPE_DETAILS_QUERY } from '../queries';
import { Card } from '@totalsoft/rocket-ui';
import DetailModalComponent from '../../edit/components/DetailModalComponent';
import { CollectionType, getListBrowserStore } from '@ts-private/list-browser';
import { ExportModalComponent } from 'components/common/export/ExportModalComponent';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { CURRENCIES_DICTIONARY_QUERY, LEASING_INTEREST_TYPES_QUERY } from 'features/common/queries';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import LeasingInterestTypeDetailItem from './LeasingInterestTypeDetailItem';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import { LEASING_INTEREST_TYPE_DETAIL_DELETED } from '../subscriptions';
import { useToast } from 'hooks/toasts/useToast';
import { useTranslation } from 'react-i18next';
import useSubscription from 'hooks/apollo/useSubscription';

export const leasingInterestTypeDetailsStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'startDate',
      caption: 'LeasingInterestTypeDetail.StartDate',
      type: 'date'
    },
    {
      name: 'endDate',
      caption: 'LeasingInterestTypeDetail.EndDate',
      type: 'date'
    },
    {
      name: 'interestTypeId',
      caption: 'LeasingInterestTypeDetail.InterestTypeName',
      type: 'select',
      underlyingType: 'number'
    },
    {
      name: 'currencyId',
      caption: 'LeasingInterestTypeDetail.Currency',
      type: 'select',
      underlyingType: 'number'
    }
  ],
  orderByFields: [
    {
      caption: 'LeasingInterestTypeDetail.StartDate',
      value: 'StartDate'
    },
    {
      caption: 'LeasingInterestTypeDetail.EndDate',
      value: 'EndDate'
    }
  ],
  pager: {
    orderBy: 'StartDate',
    direction: 'desc'
  }
});

const LeasingInterestTypeDetailListBrowser = () => {
  const addToast = useToast();
  const { t } = useTranslation();

  const fetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_LEASING_INTEREST_TYPE_DETAILS_QUERY,
        name: 'pagedLeasingInterestTypeDetails'
      },
      collectionsOptions: [
        {
          queryOptions: {
            query: LEASING_INTEREST_TYPES_QUERY,
            variables: { currencyId: null }
          },
          name: 'leasingInterestTypes',
          identifier: 'interestTypeId',
          collectionType: CollectionType.Filter,
          valueKey: 'interestTypeId',
          labelKey: 'interestTypeName'
        },
        {
          queryOptions: { query: CURRENCIES_DICTIONARY_QUERY },
          name: 'currenciesDictionary',
          identifier: 'currencyId',
          collectionType: CollectionType.Filter,
          valueKey: 'id',
          labelKey: 'code'
        }
      ]
    }),
    []
  );

  useSubscription(LEASING_INTEREST_TYPE_DETAIL_DELETED, {
    onData: ({ client }) => {
      addToast(t('LeasingInterestTypeDetail.Deleted'), 'success');
      client.cache.evict({ id: 'ROOT_QUERY', fieldName: 'pagedLeasingInterestTypeDetails' });
      client.cache.gc();
    }
  });

  const columns = useMemo(
    () => [
      { name: 'interestTypeName', caption: 'LeasingInterestTypeDetail.InterestTypeName' },
      { name: 'startDate', caption: 'LeasingInterestTypeDetail.StartDate' },
      { name: 'endDate', caption: 'LeasingInterestTypeDetail.EndDate' },
      { name: 'interestPercent', caption: 'LeasingInterestTypeDetail.InterestPercent' },
      { name: 'actions', caption: 'Actions', width: '120px' }
    ],
    []
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser
        name='leasing-interest-type-details-browser'
        store={leasingInterestTypeDetailsStore}
        ItemComponent={LeasingInterestTypeDetailItem}
        columns={columns}
      />
      <ExportModalComponent store={leasingInterestTypeDetailsStore} options={fetchingOptions} />
      <DetailModalComponent />
      <ApolloFetchingComponent store={leasingInterestTypeDetailsStore} options={fetchingOptions} />
    </Card>
  );
};

export default LeasingInterestTypeDetailListBrowser;
